import { stringNotEmptySchema } from 'schema/form/stringSchema';
import { z } from 'zod';
import { sanityImage } from './common/image';

const portableTextBlockSchema = z.object({
  _type: z.literal('block'),
  _key: z.string(),
  children: z.array(
    z.object({
      _type: z.literal('span'),
      text: z.string(),
      marks: z.array(z.string())
    })
  ),
  markDefs: z.array(z.any()),
  style: z.string()
});

export const buttons = z.object({
  skip: stringNotEmptySchema,
  ship: stringNotEmptySchema,
  cancel: stringNotEmptySchema,
  cancelButtonInModal: stringNotEmptySchema,
  reactivate: stringNotEmptySchema,
  delete: stringNotEmptySchema,
  total: stringNotEmptySchema,
  backToOrders: stringNotEmptySchema,
  backToHistory: stringNotEmptySchema,
  backToSubscription: stringNotEmptySchema,
  backToManage: stringNotEmptySchema,
  backToList: stringNotEmptySchema,
  subscribeAndSave: stringNotEmptySchema,
  subscribe: stringNotEmptySchema
});

export const tabs = z.object({
  subscriptions: stringNotEmptySchema,
  shop: stringNotEmptySchema,
  orders: stringNotEmptySchema,
  friends: stringNotEmptySchema,
  settings: stringNotEmptySchema,
  pageTitle: stringNotEmptySchema,
  sharepacks: stringNotEmptySchema
});

export const suppurtCard = z.object({
  title: stringNotEmptySchema,
  businessHours: stringNotEmptySchema,
  description: stringNotEmptySchema,
  linkFAQ: stringNotEmptySchema,
  email: stringNotEmptySchema,
  phone: stringNotEmptySchema,
  phoneHref: stringNotEmptySchema
});

export const summary = z.object({
  titleDesktop: stringNotEmptySchema,
  titleMobile: stringNotEmptySchema,
  freeGiftShipping: stringNotEmptySchema,
  subtotal: stringNotEmptySchema,
  subscriptionSubtotal: stringNotEmptySchema,
  subscriptionSubtotalMobile: stringNotEmptySchema,
  onTimeSubtotal: stringNotEmptySchema,
  shipping: stringNotEmptySchema,
  tax: stringNotEmptySchema,
  total: stringNotEmptySchema,
  discount: stringNotEmptySchema,
  discountLabel: stringNotEmptySchema,
  promoCodePlaceholder: stringNotEmptySchema,
  promoErrorMessage: stringNotEmptySchema,
  applyButton: stringNotEmptySchema
});

export const paymentInfo = z.object({
  title: stringNotEmptySchema,
  paymentMethod: stringNotEmptySchema,
  billingAddress: stringNotEmptySchema,
  editOrder: stringNotEmptySchema
});

export const shipping = z.object({
  title: stringNotEmptySchema,
  shippingMethod: stringNotEmptySchema,
  shippingAddress: stringNotEmptySchema
});

export const collapseButton = z.object({
  showLess: stringNotEmptySchema,
  showMore: stringNotEmptySchema,
  showMoreMark: stringNotEmptySchema
});

const bannerShopSchema = z
  .object({
    title: stringNotEmptySchema,
    description: stringNotEmptySchema,
    buttonText: stringNotEmptySchema,
    smallImage: sanityImage,
    image: sanityImage
  })
  .optional();

export const sharePacks = z
  .object({
    image: sanityImage
  })
  .optional();

export const banners = z.object({
  friendsBanner: stringNotEmptySchema,
  addProductDaily: stringNotEmptySchema,
  addProductPackToNext: stringNotEmptySchema,
  addPack: stringNotEmptySchema,
  expiredCard: stringNotEmptySchema,
  declinedCard: stringNotEmptySchema,
  updateMethod: stringNotEmptySchema,
  stockBunnerInSubscription: stringNotEmptySchema,
  nextOrderInfo: stringNotEmptySchema,
  nextOrderDescription: stringNotEmptySchema,
  differentAddressList: stringNotEmptySchema,
  differentAddressBannerText: stringNotEmptySchema,
  differentAddressVisitOrderPage: stringNotEmptySchema,
  differentAddressVisitSubscriptionPage: stringNotEmptySchema,
  differentAddressTextUpdate: stringNotEmptySchema,
  shop: bannerShopSchema,
  sharePacks: bannerShopSchema
});

export const orderBodyContent = z.object({
  nextOnlyLabel: stringNotEmptySchema,
  nextOnlyLabelSwitched: stringNotEmptySchema,
  outOfStockLabel: stringNotEmptySchema,
  quantityLabel: stringNotEmptySchema,
  productHiddenLabel: stringNotEmptySchema,
  productsSubtitle: stringNotEmptySchema
});

export const switchFields = z.object({
  switchToPack: stringNotEmptySchema,
  switchToPouch: stringNotEmptySchema,
  switchedToPack: stringNotEmptySchema,
  switchedToPouch: stringNotEmptySchema,
  undoSwitch: stringNotEmptySchema,
  packName: stringNotEmptySchema,
  pouchName: stringNotEmptySchema,
  switchedText: stringNotEmptySchema
});

export const status = z.object({
  fulfilled: stringNotEmptySchema,
  partial: stringNotEmptySchema,
  paid: stringNotEmptySchema,
  refunded: stringNotEmptySchema,
  partially: stringNotEmptySchema,
  unpaid: stringNotEmptySchema
});

export const addProductsCard = z.object({
  title: stringNotEmptySchema,
  subtitle: stringNotEmptySchema
});
export const v53MigrationBanner = z.object({
  title: stringNotEmptySchema,
  subtitle: stringNotEmptySchema,
  learnMoreText: stringNotEmptySchema,
  migrateActionText: stringNotEmptySchema
});
export const v53DowngradeBanner = z.object({
  title: stringNotEmptySchema,
  subtitle: stringNotEmptySchema,
  downgradeActionText: stringNotEmptySchema
});

export const emptyState = z.object({
  noPurchaseTitle: stringNotEmptySchema,
  noPurchaseText: stringNotEmptySchema,
  otpTitle: stringNotEmptySchema,
  otpText: stringNotEmptySchema
});

export const v53NotificationModal = z.object({
  title: stringNotEmptySchema,
  subtitle: stringNotEmptySchema,
  upgradedFormulaText: stringNotEmptySchema,
  buttonText: stringNotEmptySchema,
  imageDesktop: sanityImage,
  imageMobile: sanityImage
});

export const v53ScoopModal = z.object({
  title: stringNotEmptySchema,
  subtitle: stringNotEmptySchema,
  buttonText: stringNotEmptySchema,
  modalTitle: stringNotEmptySchema,
  modalDescriptionText: stringNotEmptySchema,
  modalTag: stringNotEmptySchema,
  imageDesktop: sanityImage,
  imageMobile: sanityImage
});

export const flavorConfigSchema = z.object({
  title: stringNotEmptySchema,
  color: z.string().regex(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/),
  hoverColor: z
    .string()
    .regex(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/)
    .optional()
    .nullable(),
  variantId: z.string().optional().nullable(),
  mobileImage: sanityImage.optional().nullable(),
  desktopImage: sanityImage.optional().nullable()
});

export const quickBuyAgzModal = z.object({
  title: stringNotEmptySchema,
  description: z.array(z.any()),
  buttonText: stringNotEmptySchema,
  declineLinkText: stringNotEmptySchema,
  modalTag: stringNotEmptySchema,
  flavors: z.array(flavorConfigSchema).min(1)
});

export const newFlavorsBanner = z.object({
  title: stringNotEmptySchema,
  description: z.array(z.any()),
  buttonText: stringNotEmptySchema,
  modalTag: stringNotEmptySchema,
  imageMobile: sanityImage.optional().nullable(),
  imageDesktop: sanityImage.optional().nullable()
});

export const v53TravelPacksModal = z.object({
  title: stringNotEmptySchema,
  subtitle: stringNotEmptySchema,
  buttonText: stringNotEmptySchema,
  modalTitle: stringNotEmptySchema,
  modalDescriptionText: stringNotEmptySchema,
  modalTag: stringNotEmptySchema,
  imageDesktop: sanityImage,
  imageMobile: sanityImage
});

export const v53PouchModal = z.object({
  title: stringNotEmptySchema,
  subtitle: stringNotEmptySchema,
  buttonText: stringNotEmptySchema,
  modalTitle: stringNotEmptySchema,
  modalDescriptionText: stringNotEmptySchema,
  modalTag: stringNotEmptySchema,
  imageDesktop: sanityImage,
  imageMobile: sanityImage
});

export const venmoPage = z.object({
  welcomeMessage: stringNotEmptySchema,
  subscriptionDetailsTitle: stringNotEmptySchema,
  subscriptionDetailsNextOrderDate: stringNotEmptySchema,
  subscriptionDetailsFrequency: stringNotEmptySchema,
  contactInformationTitle: stringNotEmptySchema,
  shippingAddressTitle: stringNotEmptySchema,
  shippingMethodTitle: stringNotEmptySchema,
  paymentMethodTitle: stringNotEmptySchema,
  billingAddressTitle: stringNotEmptySchema,
  updateSubscriptionButton: stringNotEmptySchema,
  cancelSubscriptionButton: stringNotEmptySchema,
  venmoSupportMessage: stringNotEmptySchema,
  venmoSupportCard: z.object({
    title: stringNotEmptySchema,
    businessHours: stringNotEmptySchema,
    description: stringNotEmptySchema,
    linkFAQ: stringNotEmptySchema,
    phone: stringNotEmptySchema,
    phoneHref: stringNotEmptySchema
  }),
  venmoCancellationModal: z.object({
    modalHeader: stringNotEmptySchema,
    cancellationText: stringNotEmptySchema,
    contactSupportText: stringNotEmptySchema,
    contactSupportHref: stringNotEmptySchema,
    resubscribeButton: stringNotEmptySchema,
    cancelButton: stringNotEmptySchema
  })
});

export const sepaPage = z.object({
  welcomeMessage: stringNotEmptySchema,
  subscriptionDetailsTitle: stringNotEmptySchema,
  subscriptionDetailsNextOrderDate: stringNotEmptySchema,
  subscriptionDetailsFrequency: stringNotEmptySchema,
  contactInformationTitle: stringNotEmptySchema,
  shippingAddressTitle: stringNotEmptySchema,
  shippingMethodTitle: stringNotEmptySchema,
  paymentMethodTitle: stringNotEmptySchema,
  billingAddressTitle: stringNotEmptySchema,
  updateSubscriptionButton: stringNotEmptySchema,
  cancelSubscriptionButton: stringNotEmptySchema,
  sepaSupportMessage: stringNotEmptySchema,
  sepaSupportCard: z.object({
    title: stringNotEmptySchema,
    businessHours: stringNotEmptySchema,
    description: stringNotEmptySchema,
    linkFAQ: stringNotEmptySchema,
    phone: stringNotEmptySchema,
    phoneHref: stringNotEmptySchema
  }),
  sepaCancellationModal: z.object({
    modalHeader: stringNotEmptySchema,
    cancellationText: stringNotEmptySchema,
    contactSupportText: stringNotEmptySchema,
    contactSupportHref: stringNotEmptySchema,
    resubscribeButton: stringNotEmptySchema,
    cancelButton: stringNotEmptySchema
  })
});

export const annualPlanDetailPage = z.object({
  collectionHandle: stringNotEmptySchema,
  chargeStartingPrefix: z.array(portableTextBlockSchema),
  chargeFrequency: z.array(portableTextBlockSchema),
  discountDescriptor: z.array(portableTextBlockSchema),
  flavorsCounter: z.array(portableTextBlockSchema),
  freeLabel: z.array(portableTextBlockSchema)
});

export const commonContentDataSchema = z.object({
  _type: z.literal('commonContent'),
  tabs,
  orderBodyContent,
  collapseButton,
  shipping,
  paymentInfo,
  summary,
  suppurtCard,
  banners,
  switchFields,
  buttons,
  status,
  sharePacks,
  addProductsCard: addProductsCard.nullish(),
  v53NotificationModal: v53NotificationModal.nullish(),
  annualPlanDetailPage: annualPlanDetailPage.nullish(),
  v53ScoopModal: v53ScoopModal.nullish(),
  venmoPage: venmoPage.nullish(),
  sepaPage: sepaPage.nullish(),
  v53MigrationBanner: v53MigrationBanner.nullish(),
  v53DowngradeBanner: v53DowngradeBanner.nullish(),
  emptyState: emptyState.nullish(),
  v53TravelPacksModal: v53TravelPacksModal.nullish(),
  v53PouchModal: v53PouchModal.nullish(),
  quickBuyAgzModal: quickBuyAgzModal.nullish(),
  newFlavorsBanner: newFlavorsBanner.nullish()
});

export type CommonContent = z.infer<typeof commonContentDataSchema>;
export type OrderBodyContent = z.infer<typeof orderBodyContent>;
export type CollapseContent = z.infer<typeof collapseButton>;
export type BannersContent = z.infer<typeof banners>;
export type ButtonsContent = z.infer<typeof buttons>;
export type StatusList = z.infer<typeof status>;
export type VenmoPage = z.infer<typeof venmoPage>;
export type SepaPage = z.infer<typeof sepaPage>;
